import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { debounceTime, filter, map, take, tap } from 'rxjs/operators';

import { Params } from '@angular/router';

import { Dispatch } from '@ngxs-labs/dispatch-decorator';
import { Navigate } from '@ngxs/router-plugin';
import { Select } from '@ngxs/store';

import { lastValue } from '@app/shared/operators/last-value.operator';
import {
  API_DEFAULT_SEARCH_DEBOUNCE,
  ApiFilterValues,
  ApiGetRequest,
  ApiMutatorSettings,
  ApiResponse,
} from '../models/api.models';
import { RouterNavState } from '../store/states/router-nav/router-nav.state';

export abstract class ApiGetMutator<
  T extends object,
  Req extends Partial<ApiGetRequest<T>>,
  Res extends ApiResponse,
> extends Observable<Res> {
  @Dispatch()
  readonly updateSearchQuery = (searchParam: string, search: ApiGetRequest<T>['searchTerm'] | undefined) =>
    new Navigate([], { [searchParam]: search || null }, { queryParamsHandling: 'merge', replaceUrl: true });

  @Select(RouterNavState.queryParams)
  readonly queryParams$!: Observable<Params>;

  protected readonly refresh$ = new BehaviorSubject<void>(void 0);

  protected readonly refreshing = new BehaviorSubject<boolean>(false);

  protected readonly loading = new BehaviorSubject(false);

  protected readonly firstLoad = new BehaviorSubject(true);

  readonly include$ = new BehaviorSubject<ApiGetRequest<T>['include'] | undefined>(void 0);

  readonly exclude$ = new BehaviorSubject<ApiGetRequest<T>['exclude'] | undefined>(void 0);

  readonly searchTerm$ = new BehaviorSubject<ApiGetRequest<T>['searchTerm'] | undefined>(
    lastValue(this.queryParams$)[this.settings.searchParam],
  );

  readonly searchFields$ = new BehaviorSubject<ApiGetRequest<T>['searchFields'] | undefined>(void 0);

  readonly sortValues$ = new BehaviorSubject<ApiGetRequest<T>['sortValues'] | undefined>(void 0);

  readonly searchValues$ = new BehaviorSubject<ApiGetRequest<T>['searchValues'] | undefined>(void 0);

  readonly filterValues$ = new BehaviorSubject<ApiFilterValues<T> | undefined>(void 0);

  readonly fixedFilterValues$ = new BehaviorSubject<ApiFilterValues<T> | undefined>(void 0);

  readonly groupBy$ = new BehaviorSubject<ApiGetRequest<T>['groupBy'] | undefined>(void 0);

  readonly loading$: Observable<boolean> = this.loading.asObservable().pipe(debounceTime(1));

  readonly refreshing$: Observable<boolean> = this.refreshing.asObservable().pipe(debounceTime(1));

  readonly firstLoad$: Observable<boolean> = this.firstLoad.asObservable();

  protected inputs = combineLatest([
    combineLatest([this.include$, this.exclude$, this.groupBy$]),
    combineLatest([
      this.searchTerm$.pipe(
        map((term) => (term != null && term.toString().trim()) || void 0),
        tap((term) => {
          this.loading.next(true);

          if (this.settings.searchParam) {
            this.updateSearchQuery(this.settings.searchParam, term);
          }
        }),
        debounceTime(this.settings.searchDebounceTime || API_DEFAULT_SEARCH_DEBOUNCE),
      ),
      this.searchFields$,
    ]),
    combineLatest([this.sortValues$, this.searchValues$, this.filterValues$, this.fixedFilterValues$]),
  ]);

  abstract readonly source: Observable<Res>;

  protected constructor(
    protected api: (req: Partial<Req>) => Observable<Res>,
    protected init: Partial<Req> = {},
    protected settings: ApiMutatorSettings = {},
  ) {
    super();
  }

  refresh(): Observable<void> {
    this.refresh$.next(void 0);
    this.refreshing.next(true);

    return this.loading$.pipe(
      filter((loading) => !loading),
      take(1),
      tap(() => this.refreshing.next(false)),
      map(() => void 0),
    );
  }

  reset(initialReset?: boolean): void {
    this.include$.next(this.init.include);
    this.exclude$.next(this.init.exclude);
    this.searchTerm$.next(
      initialReset ? lastValue(this.queryParams$)[this.settings.searchParam] : this.init.searchTerm,
    );
    this.searchFields$.next(this.init.searchFields);
    this.sortValues$.next(this.init.sortValues);
    this.searchValues$.next(this.init.searchValues);
    this.filterValues$.next(this.init.filterValues);
    this.fixedFilterValues$.next(this.init.fixedFilterValues);
  }

  setLoading(loading: boolean): void {
    this.loading.next(loading);
  }
}
